import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  Package,
  AlertCircle,
  CheckCircle,
  Eye,
  Download,
  Loader2,
  FileText,
  Truck,
} from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { API_BASE_URL } from "../../utils/apiConfig";

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries?: string[];
}

interface Order {
  id: number;
  trackingId: string;
  name: string;
  phone: string;
  city: string;
  country: string;
  price: number;
  shippingDate: string;
  pickupDate?: string; // Custom pickup date from modal
  pickupDateNotes?: string; // Reason for custom pickup date
  shippingCompany: string;
  status: number;
  bexStatus?: {
    code: number;
    name: string;
    description: string;
  };
  easyMailStatus?: {
    code: string;
    name: string;
    description: string;
  };
  shippingStatus?: string;
}

interface TodaysPickupProps {
  currentUser: User;
  onOpenPdfPreview: (
    pdfBase64: string,
    pickupListNo: string,
    shippingCompany: string
  ) => void;
  onStatsUpdate?: (stats: {
    totalVouchers?: number;
    printed?: number;
    unprinted?: number;
  }) => void;
}

const TodaysPickup: React.FC<TodaysPickupProps> = ({
  currentUser,
  onOpenPdfPreview,
  onStatsUpdate,
}) => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalVouchers, setTotalVouchers] = useState<number>(0);
  const [deliveredStats, setDeliveredStats] = useState<{
    Transit: number;
    Delivered: number;
    Returned: number;
    Complicated: number;
  }>({ Transit: 0, Delivered: 0, Returned: 0, Complicated: 0 });
  const [issuing, setIssuing] = useState<boolean>(false);
  const [printing, setPrinting] = useState<boolean>(false);
  const [existingPickupList, setExistingPickupList] = useState<{
    pickupListNo: string;
    pickupDate: string;
    issuedAt: string;
    status: string;
  } | null>(null);
  const [unprintedVouchers, setUnprintedVouchers] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const apiBaseUrl = API_BASE_URL;

  // Calculate statistics based on shipping statuses
  const stats = useMemo(() => {
    // Align cards with DeliveredStatistics using backend delivered-stats for the exact date
    return {
      totalShipments: orders.length,
      registered: 0, // not tracked in delivered-stats; keep 0 for now
      inTransit: deliveredStats.Transit,
      delivered: deliveredStats.Delivered,
      returned: deliveredStats.Returned,
    };
  }, [orders, deliveredStats]);

  useEffect(() => {
    loadDateData();
  }, [selectedDate]);

  const loadDateData = async () => {
    setLoading(true);
    setErrorMessage("");
    setUnprintedVouchers([]);

    try {
      // Load vouchers for the selected date
      const vouchersResponse = await fetch(
        `${apiBaseUrl}/api/shipping/acs/pickup-lists/today?date=${selectedDate}`
      );
      const vouchersResult = await vouchersResponse.json();

      if (vouchersResult.success) {
        setOrders(vouchersResult.data.orders || []);
        setTotalVouchers(vouchersResult.data.totalVouchers || 0);
        if (onStatsUpdate) {
          // Provisional printed count: if a pickup list exists for selected date, consider all printed after printing step.
          // Before issuing/printing, treat as unprinted.
          onStatsUpdate({
            totalVouchers: vouchersResult.data.totalVouchers || 0,
          });
        }
      } else {
        setErrorMessage(vouchersResult.error || "Failed to load shipments");
      }

      // Fetch Delivered Statistics for this exact date
      const params = new URLSearchParams();
      params.append("dateRange", "custom");
      params.append("customStartDate", selectedDate);
      params.append("customEndDate", selectedDate);
      const deliveredResp = await fetch(
        `${apiBaseUrl}/api/shipping/acs/delivered-stats?${params.toString()}`
      );
      const deliveredJson = await deliveredResp.json();
      if (deliveredJson.success) {
        setDeliveredStats(
          deliveredJson.data.summary || {
            Transit: 0,
            Delivered: 0,
            Returned: 0,
            Complicated: 0,
          }
        );
      }

      // Check if a pickup list already exists for this date
      const pickupListResponse = await fetch(
        `${apiBaseUrl}/api/shipping/acs/pickup-lists/by-date?date=${selectedDate}`
      );
      const pickupListResult = await pickupListResponse.json();

      if (pickupListResult.success && pickupListResult.data) {
        setExistingPickupList(pickupListResult.data);
        if (onStatsUpdate) {
          // If list exists, assume printed == total vouchers for summary
          onStatsUpdate({
            printed: vouchersResult.data?.totalVouchers || 0,
            unprinted: 0,
          });
        }
      } else {
        setExistingPickupList(null);
        if (onStatsUpdate) {
          onStatsUpdate({
            printed: 0,
            unprinted: vouchersResult.data?.totalVouchers || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error loading date data:", error);
      setErrorMessage("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleIssuePickupList = async () => {
    if (totalVouchers === 0) {
      alert("No vouchers available for this date");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to issue a pickup list for ${selectedDate}?\n\nThis will finalize ${totalVouchers} voucher(s) for courier pickup.`
      )
    ) {
      return;
    }

    setIssuing(true);
    setErrorMessage("");
    setUnprintedVouchers([]);

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/shipping/acs/pickup-lists/issue`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pickupDate: selectedDate,
            myData: null, // Use null to finalize all users' shipments
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setExistingPickupList({
          pickupListNo: result.data.pickupListNo,
          pickupDate: result.data.pickupDate,
          issuedAt: result.data.issuedAt,
          status: "issued",
        });
        setErrorMessage("");
        setUnprintedVouchers([]);
      } else {
        // Handle unprinted vouchers
        if (result.error === "Unprinted vouchers found") {
          setUnprintedVouchers(result.unprintedVouchers || []);
          setErrorMessage(
            `Cannot issue pickup list: Found ${result.unprintedCount} unprinted voucher(s). Please print or delete them first.`
          );
        } else {
          setErrorMessage(result.error || "Failed to issue pickup list");
        }
      }
    } catch (error) {
      console.error("Error issuing pickup list:", error);
      setErrorMessage("Failed to issue pickup list. Please try again.");
    } finally {
      setIssuing(false);
    }
  };

  const handlePreviewPickupList = async () => {
    if (!existingPickupList) {
      alert("No pickup list available for this date");
      return;
    }

    setPrinting(true);
    setErrorMessage("");

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/shipping/acs/pickup-lists/print`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pickupListNo: existingPickupList.pickupListNo,
            pickupDate: existingPickupList.pickupDate,
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data.pdfBase64) {
        onOpenPdfPreview(
          result.data.pdfBase64,
          existingPickupList.pickupListNo,
          "acs"
        );
      } else {
        setErrorMessage(result.error || "Failed to load pickup list PDF");
      }
    } catch (error) {
      console.error("Error loading pickup list PDF:", error);
      setErrorMessage("Failed to load pickup list PDF. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5" style={{ color: "#264a3e" }} />
            <label className="text-sm font-medium text-gray-700">
              Pickup Date:
            </label>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
          />
        </div>
      </div>

      {/* Statistics Cards - Horizontal Scroll on Mobile */}
      <div className="lg:hidden overflow-x-auto -mx-4 px-4 pb-2">
        <div className="flex space-x-3 min-w-max">
          {/* Total Shipments */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Truck className="w-5 h-5" style={{ color: "#264a3e" }} />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">
                  Total Shipments
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {stats.totalShipments}
                </p>
              </div>
            </div>
          </div>

          {/* Registered */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="w-5 h-5" style={{ color: "#264a3e" }} />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Registered</p>
                <p className="text-lg font-semibold" style={{ color: "#264a3e" }}>
                  {stats.registered}
                </p>
              </div>
            </div>
          </div>

          {/* In Transit */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Truck className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">In Transit</p>
                <p className="text-lg font-semibold text-yellow-600">
                  {stats.inTransit}
                </p>
              </div>
            </div>
          </div>

          {/* Delivered */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Delivered</p>
                <p className="text-lg font-semibold text-green-600">
                  {stats.delivered}
                </p>
              </div>
            </div>
          </div>

          {/* Returned */}
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Returned</p>
                <p className="text-lg font-semibold text-red-600">
                  {stats.returned}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Statistics Cards */}
      <div className="hidden lg:grid grid-cols-5 gap-4">
        {/* Total Shipments */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Truck className="w-6 h-6" style={{ color: "#264a3e" }} />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">
                Total Shipments
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {stats.totalShipments}
              </p>
            </div>
          </div>
        </div>

        {/* Registered */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Package className="w-6 h-6" style={{ color: "#264a3e" }} />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Registered</p>
              <p className="text-lg font-semibold" style={{ color: "#264a3e" }}>
                {stats.registered}
              </p>
            </div>
          </div>
        </div>

        {/* In Transit */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Truck className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">In Transit</p>
              <p className="text-lg font-semibold text-yellow-600">
                {stats.inTransit}
              </p>
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Delivered</p>
              <p className="text-lg font-semibold text-green-600">
                {stats.delivered}
              </p>
            </div>
          </div>
        </div>

        {/* Returned */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-500">Returned</p>
              <p className="text-lg font-semibold text-red-600">
                {stats.returned}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
            {unprintedVouchers.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-red-800 mb-1">
                  Unprinted Vouchers:
                </p>
                <ul className="list-disc list-inside text-xs text-red-700 space-y-1">
                  {unprintedVouchers.map((voucher, index) => (
                    <li key={index}>
                      {voucher.Unprinted_Vouchers || JSON.stringify(voucher)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pickup List Status */}
      {existingPickupList && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-lg font-semibold text-green-900">
                  Pickup List Issued
                </p>
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-green-600 font-medium">
                      Pickup List Number
                    </p>
                    <p className="text-xl font-mono font-bold text-green-900 mt-1">
                      {existingPickupList.pickupListNo}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium">
                      Pickup Date
                    </p>
                    <p className="text-sm font-semibold text-green-800 mt-1">
                      {new Date(
                        existingPickupList.pickupDate
                      ).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium">
                      Issued At
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {new Date(existingPickupList.issuedAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium">
                      Total Vouchers
                    </p>
                    <p className="text-xl font-bold text-green-900 mt-1">
                      {totalVouchers}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handlePreviewPickupList}
              disabled={printing}
              className="ml-4 flex items-center space-x-2 px-6 py-3 rounded-md text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#264a3e" }}
            >
              {printing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5" />
                  <span>Preview PDF</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Actions - Only show Issue button if no pickup list exists */}
      {!existingPickupList && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Issue Pickup List
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Create a pickup list for all {totalVouchers} voucher(s) on{" "}
                {selectedDate}
              </p>
            </div>

            <button
              onClick={handleIssuePickupList}
              disabled={issuing || totalVouchers === 0}
              className="flex items-center justify-center space-x-2 px-6 py-3 rounded-md text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              style={{ backgroundColor: "#264a3e" }}
            >
              {issuing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Issuing...</span>
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  <span>Issue Pickup List</span>
                </>
              )}
            </button>
          </div>

          {totalVouchers === 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                No vouchers available for this date. Create shipments first.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Vouchers List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Vouchers for {selectedDate}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                All ACS vouchers created on this date
              </p>
            </div>
            <div className="text-xs sm:text-sm text-gray-600">
              <span className="font-semibold">{totalVouchers}</span> voucher(s)
              found
            </div>
          </div>
        </div>

        <div className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No shipments found for this date</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="pl-4 pr-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Tracking ID
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Pickup
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order) => {
                    const hasCustomPickupDate =
                      order.pickupDate && order.pickupDate !== selectedDate;
                    const isScheduledForLater =
                      order.pickupDate &&
                      new Date(order.pickupDate) > new Date(selectedDate);

                    return (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="pl-4 pr-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-900">
                          #{order.id}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-mono text-gray-700">
                          {order.trackingId}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-900">
                          <div>{order.name}</div>
                          <div className="text-xs text-gray-500">
                            {order.phone}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-700">
                          {order.city}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
                          {order.pickupDate ? (
                            <div>
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-3 h-3 text-orange-600" />
                                <span className="text-orange-600 font-medium">
                                  {new Date(
                                    order.pickupDate
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                              </div>
                              {order.pickupDateNotes && (
                                <div
                                  className="text-xs text-gray-500 mt-1"
                                  title={order.pickupDateNotes}
                                >
                                  {order.pickupDateNotes.length > 30
                                    ? order.pickupDateNotes.substring(0, 30) +
                                      "..."
                                    : order.pickupDateNotes}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-green-600 text-xs">
                              Default
                            </span>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-900">
                          €{order.price.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodaysPickup;
